import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import Avatar from '../../../components/avatar/Avatar';
import IconButton from '../../../components/button/IconButton';
import ToolTip from '../../../components/tooltip/ToolTip';

/**
 * タイムラインのコメントピンレーン。
 * タイムコード付きコメントの位置に投稿者アバターを配置し、
 * ホバー / クリックでコメント内容のポップオーバーを表示する。
 * クリックはあわせて親へ通知し、その位置へのシークに使う。
 * ポップオーバー右上から解決済み / 進行中のトグルもできる。
 */

export interface TimelinePin {
  commentId: string;
  authorName: string;
  avatarUrl: string | null;
  timeSeconds: number;
  /** "M:SS" / "M:SS.FF" / 絶対フレーム "300F" 表示用ラベル */
  timecodeLabel: string;
  text: string;
  /** 解決済みコメントか（タイムライン側で表示切替に使う） */
  isResolved: boolean;
}

export interface CommentPinLayerProps {
  pins: TimelinePin[];
  pixelsPerSecond: number;
  height: number;
  /** ピンクリック時（シーク用） */
  onPinSelect: (_pin: TimelinePin) => void;
  /** ポップオーバーの解決済み / 進行中トグル。未指定ならアイコン非表示 */
  onResolveToggle?: (_pin: TimelinePin) => void;
}

const PIN_SIZE = 20;
/** これ以上近いピンは横にずらして重ね表示する */
const OVERLAP_THRESHOLD = PIN_SIZE * 0.7;
const OVERLAP_SHIFT = 9;

const PinLane = styled.div<{ $height: number }>`
  position: relative;
  height: ${p => p.$height}px;
  background: #fff;
`;

const PinWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: scale 0.15s ease;

  &:hover {
    scale: 1.15;
    z-index: 5;
  }
`;

const PinStem = styled.div`
  position: absolute;
  left: 50%;
  top: 100%;
  width: 1px;
  height: 4px;
  background: var(--soremo-light-gray);
`;

const Popover = styled.div`
  position: fixed;
  transform: translate(-50%, calc(-100% - 10px));
  width: max-content;
  max-width: 280px;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 8px 12px;
  z-index: 1000;
  pointer-events: auto;
`;

const PopoverText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;

const TimecodeChip = styled.span.attrs({ className: 'bodytext-11' })`
  display: inline-block;
  background: var(--soremo-light-gray);
  color: #fff;
  line-height: 150%;
  padding: 0 4px;
  border-radius: 3px;
  font-variant-numeric: tabular-nums;
`;

interface ActivePopover {
  /** 表示対象ピンの commentId。ピン内容は描画時に pins から引き直す（スナップショットは持たない） */
  commentId: string;
  anchorRect: DOMRect;
  /** クリックで固定されたか（true なら外側クリックまで維持） */
  pinned: boolean;
}

const CommentPinLayer = memo(function CommentPinLayer({
  pins,
  pixelsPerSecond,
  height,
  onPinSelect,
  onResolveToggle,
}: CommentPinLayerProps) {
  const [popover, setPopover] = useState<ActivePopover | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 解決トグル後も最新状態を映すよう、表示中ピンは pins から引き直す。
  // （解決済み非表示中にピンが消えた場合はポップオーバーも閉じる）
  const displayedPin = popover
    ? pins.find(p => p.commentId === popover.commentId) ?? null
    : null;
  useEffect(() => {
    setPopover(prev =>
      prev && !pins.some(p => p.commentId === prev.commentId) ? null : prev,
    );
  }, [pins]);

  // 時間順に整列し、近接ピンは横に少しずらして重ね表示する
  const placedPins = useMemo(() => {
    const sorted = [...pins].sort((a, b) => a.timeSeconds - b.timeSeconds);
    let prevX = -Infinity;
    return sorted.map(pin => {
      const baseX = pin.timeSeconds * pixelsPerSecond;
      const x = baseX <= prevX + OVERLAP_THRESHOLD ? prevX + OVERLAP_SHIFT : baseX;
      prevX = x;
      return { pin, x };
    });
  }, [pins, pixelsPerSecond]);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => {
      setPopover(prev => (prev?.pinned ? prev : null));
    }, 150);
  }, [cancelHide]);

  useEffect(() => () => cancelHide(), [cancelHide]);

  // 固定ポップオーバーは外側クリックで閉じる
  useEffect(() => {
    if (!popover?.pinned) return;
    const onPointerDown = () => setPopover(null);
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [popover?.pinned]);

  const handleEnter = useCallback((pin: TimelinePin, e: React.MouseEvent) => {
    cancelHide();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopover(prev =>
      prev?.pinned ? prev : { commentId: pin.commentId, anchorRect: rect, pinned: false },
    );
  }, [cancelHide]);

  const handleClick = useCallback((pin: TimelinePin, e: React.MouseEvent) => {
    // タイムラインのスクラブと外側クリック判定に伝播させない
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopover({ commentId: pin.commentId, anchorRect: rect, pinned: true });
    onPinSelect(pin);
  }, [onPinSelect]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // スクラブ開始と外側クリック判定を抑止
    e.stopPropagation();
  }, []);

  return (
    <PinLane $height={height}>
      {placedPins.map(({ pin, x }) => (
        <PinWrapper
          key={pin.commentId}
          style={{ left: x, opacity: pin.isResolved ? 0.5 : 1 }}
          onMouseEnter={e => handleEnter(pin, e)}
          onMouseLeave={scheduleHide}
          onClick={e => handleClick(pin, e)}
          onPointerDown={handlePointerDown}
        >
          <Avatar size={PIN_SIZE} src={pin.avatarUrl ?? undefined} name={pin.authorName} />
          <PinStem />
        </PinWrapper>
      ))}

      {popover && displayedPin && createPortal(
        <Popover
          style={{
            left: popover.anchorRect.left + popover.anchorRect.width / 2,
            top: popover.anchorRect.top,
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          onPointerDown={e => e.stopPropagation()}
        >
          <div className="u-row u-items-center" style={{ gap: 8, marginBottom: 4 }}>
            <Avatar size={20} src={displayedPin.avatarUrl ?? undefined} name={displayedPin.authorName} />
            <span className="label8 u-text-black">{displayedPin.authorName}</span>
            <TimecodeChip>{displayedPin.timecodeLabel}</TimecodeChip>
            {onResolveToggle && (
              <span style={{ marginLeft: 'auto' }}>
                <ToolTip
                  description={displayedPin.isResolved ? '進行中に戻す' : '解決済みにする'}
                  placement="top"
                  appendTo="parent"
                >
                  <IconButton
                    icon="task_alt"
                    size={16}
                    filled={displayedPin.isResolved}
                    color={displayedPin.isResolved ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)'}
                    onClick={() => onResolveToggle(displayedPin)}
                  />
                </ToolTip>
              </span>
            )}
          </div>
          <PopoverText className="bodytext-11 u-text-black">{displayedPin.text}</PopoverText>
        </Popover>,
        document.body,
      )}
    </PinLane>
  );
});

export default CommentPinLayer;
